import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchHealth, fetchPipelineHealth, fetchSyncStatus } from "../lib/api";
import { relativeTime } from "../lib/dates";
import { roleCan, useRole } from "../lib/roles";
import { PageSkeleton } from "../components/PageSkeleton";
import { AccessDenied } from "./NewsletterEditorPage";
import { CoverageChip, PageHeader, SectionHeader, StatusBadge, Surface } from "../components/primitives";

type Tone = "success" | "warn" | "info" | "muted";

interface Check {
  id: string;
  title: string;
  detail: string;
  tone: Tone;
  score: number;
  why: string;
}

export function HealthPage() {
  const { role } = useRole();
  const health = useQuery({ queryKey: ["health"], queryFn: fetchHealth });
  const sync = useQuery({ queryKey: ["sync"], queryFn: fetchSyncStatus });
  const pipeline = useQuery({ queryKey: ["pipeline"], queryFn: fetchPipelineHealth });
  const [openId, setOpenId] = useState<string>("warehouse");

  const checks = useMemo<Check[]>(() => {
    const delivery = health.data?.delivery;
    const emailOk = health.data?.email === true;
    const consentMissing = pipeline.data?.consent_missing ?? 0;
    const profiles = pipeline.data?.profiles ?? 1;
    const consentPct = profiles ? ((pipeline.data?.consent_granted ?? 0) / profiles) * 100 : 0;
    const syncAge = sync.data?.last_success ? Date.now() - Date.parse(sync.data.last_success) : Infinity;
    const warehouseFresh = syncAge < 48 * 60 * 60 * 1000;
    return [
      {
        id: "warehouse",
        title: "Subscriber list",
        detail: warehouseFresh ? `Updated ${relativeTime(sync.data?.last_success)}` : "Stale — last success is over 48 hours ago",
        tone: warehouseFresh ? "success" : "warn",
        score: warehouseFresh ? 100 : 40,
        why: warehouseFresh
          ? `${(sync.data?.rows_loaded ?? 0).toLocaleString()} rows loaded in the last sync.`
          : "Subscriber counts and audiences may be out of date until the next successful sync.",
      },
      {
        id: "delivery",
        title: "Sending to subscribers",
        detail: delivery?.ready ? `Connected · ${delivery.from}` : "Not connected yet",
        tone: delivery?.ready ? "success" : "warn",
        score: delivery?.ready ? 100 : 30,
        why: delivery?.ready
          ? "Broadcasts and automations can reach subscribers through Amazon SES."
          : `Still needs ${(delivery?.missing ?? ["the delivery service"]).join(", ")}. Until then nothing reaches subscribers; see the checklist in Broadcasts.`,
      },
      {
        id: "email",
        title: "Test emails",
        detail: emailOk ? `Connected · ${health.data?.email_from}` : "Not connected",
        tone: emailOk ? "success" : "warn",
        score: emailOk ? 100 : 20,
        why: emailOk
          ? "Used only to send a test to your own inbox. Sending to subscribers is the check above."
          : "Test sends need the Cloudflare Email binding on the Worker.",
      },
      {
        id: "consent",
        title: "Marketing consent (Altibbi members)",
        detail: `${consentPct.toFixed(1)}% granted · ${consentMissing.toLocaleString()} missing`,
        tone: consentPct > 50 ? "success" : "warn",
        score: Math.min(100, Math.round(consentPct)),
        why: "For the wider Altibbi member list, not newsletter subscribers: members without a marketing opt-in are never emailed.",
      },
    ];
  }, [health.data, sync.data, pipeline.data]);

  const score = Math.round(checks.reduce((s, c) => s + c.score, 0) / checks.length);
  const open = checks.find((c) => c.id === openId) ?? checks[0];
  const coverage = checks.some((c) => c.tone === "warn") ? "partial" : checks.some((c) => c.tone === "info") ? "mixed" : "complete";

  if (!roleCan(role, "health.view")) return <AccessDenied need="Admins" />;
  if (health.isLoading || pipeline.isLoading) return <PageSkeleton />;

  return (
    <div style={{ display: "grid", gap: "var(--ui-space-3)" }}>
      <PageHeader
        title="Data health"
        meta={
          <>
            <CoverageChip
              status={coverage}
              title="Whether the list is fresh and sending is connected."
            >
              {coverage === "complete" ? "All good" : coverage === "mixed" ? "Mostly good" : "Needs attention"} · list synced {sync.data?.last_success ? relativeTime(sync.data.last_success) : "at an unknown time"}
            </CoverageChip>
            <span>Is the subscriber list fresh, and can newsletters reach people?</span>
          </>
        }
      />

      <Surface style={{ overflow: "hidden" }}>
        <div className="nl-health-head">
          <div>
            <div className="nl-stat-mini-label">Workspace score</div>
            <div className="nl-health-score-value tabnum">{score}</div>
          </div>
          <p className="nl-health-head-copy">
            {score >= 80 ? "Everything needed to send is in place." : "Needs attention before newsletters can reach subscribers."}
            {" "}Choose a check to see why.
          </p>
        </div>

        <div className="nl-check-list">
          {checks.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`nl-check-row${openId === c.id ? " is-open" : ""}`}
              onClick={() => setOpenId(c.id)}
            >
              <StatusBadge tone={c.tone}>{c.tone === "success" ? "Pass" : c.tone === "warn" ? "Watch" : "Info"}</StatusBadge>
              <div className="nl-check-copy">
                <div className="nl-check-title">{c.title}</div>
                <div className="nl-check-detail">{c.detail}</div>
              </div>
              <span className="nl-check-score tabnum">{c.score}</span>
              <div className="nl-check-bar"><span style={{ width: `${c.score}%` }} /></div>
            </button>
          ))}
        </div>

        <SectionHeader compact title={open.title} meta="What this means for sending" />
        <div style={{ padding: "var(--ui-space-3) var(--ui-space-4) var(--ui-space-4)" }}>
          <p style={{ margin: "0 0 12px", color: "var(--c-t2)", fontSize: "var(--ui-text-body)", lineHeight: 1.45 }}>{open.why}</p>
        </div>
      </Surface>
    </div>
  );
}
